
import { 
  getFirestore, 
  initializeFirestore, 
  doc, 
  collection, 
  onSnapshot, 
  setDoc, 
  deleteDoc, 
  getDoc,
  getDocs,
  CACHE_SIZE_UNLIMITED,
  persistentLocalCache,
  persistentMultipleTabManager,
  DocumentReference,
  CollectionReference,
  Query,
  setLogLevel
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { DiscussionThread, ArchiveFile, Post } from '../types';
import { app } from './auth';
import firebaseConfig from '../firebase-applet-config.json';

let db: any = null;
let auth: any = null;

export const safeStringify = (obj: any, indent?: number): string => {
  const cache = new Set();
  try {
    return JSON.stringify(
      obj,
      (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (typeof window !== 'undefined' && (value === window || value instanceof Node || value instanceof Event)) {
            return '[DOM Node/Event]';
          }
          if (cache.has(value)) {
            return '[Circular]';
          }
          cache.add(value);
        }
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack,
          };
        }
        return value;
      },
      indent
    );
  } catch (err) {
    return String(obj);
  }
};

if (app) {
  try {
    setLogLevel('error');
    // Initialize Firestore with default settings (auto-detect polling/sockets)
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager()
      })
    });
    auth = getAuth(app);
  } catch (e: any) {
    // If Firestore was already initialized (e.g. during hot reload), use existing instance
    if (e.code === 'failed-precondition') {
        db = getFirestore(app);
        auth = getAuth(app);
        console.warn("Firestore already initialized, using existing instance.");
    } else {
        console.error("Firebase Firestore Initialization Error:", e.message || e);
    }
  }
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth?.currentUser?.uid,
      email: auth?.currentUser?.email,
      emailVerified: auth?.currentUser?.emailVerified,
      isAnonymous: auth?.currentUser?.isAnonymous,
      tenantId: auth?.currentUser?.tenantId,
      providerInfo: auth?.currentUser?.providerData?.map((provider: any) => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', safeStringify(errInfo, 2));
  throw new Error(safeStringify(errInfo));
}

const isPlainObject = (obj: any) => {
  return typeof obj === 'object' && obj !== null && obj.constructor === Object;
};

const toPlainData = (snapshot: any) => {
    if (!snapshot.exists()) return null;
    const data = snapshot.data();

    // This recursive function will sanitize any value from Firestore.
    // It converts Timestamps, and recursively cleans Arrays and plain Objects.
    // Any other object type (like a DocumentReference) will be stripped out (returned as undefined).
    const sanitize = (val: any): any => {
        if (val === null || val === undefined) {
            return val;
        }
        
        // Handle Firestore Timestamps
        if (val && typeof val.toDate === 'function') {
            return val.toDate().getTime();
        }

        // Handle Arrays recursively
        if (Array.isArray(val)) {
            // Sanitize each item and filter out any that become undefined
            return val.map(sanitize).filter(item => item !== undefined);
        }
        
        // Handle Plain Objects recursively
        if (isPlainObject(val)) {
            const plain: { [key: string]: any } = {};
            for (const key in val) {
                // Ensure it's an own property before processing
                if (Object.prototype.hasOwnProperty.call(val, key)) {
                    const sanitizedValue = sanitize(val[key]);
                    // Omit keys where the value sanitizes to undefined
                    if (sanitizedValue !== undefined) {
                        plain[key] = sanitizedValue;
                    }
                }
            }
            return plain;
        }
        
        // Keep primitives (string, number, boolean)
        if (typeof val !== 'object') {
            return val;
        }
        
        // For any other type of object that made it this far, discard it.
        // This is crucial for stripping out things like DocumentReferences, GeoPoints, etc.
        return undefined;
    };
    
    return sanitize(data);
};

// Helper to check if we can write to Firestore
// Prevents "Missing or insufficient permissions" when using Dev Admin fallback
const canWriteToFirestore = (): boolean => {
    if (!auth?.currentUser) {
        console.warn("Firestore write skipped: No authenticated user (Dev Mode active). Data will be local-only.");
        return false;
    }
    return true;
};

// --- FIRESTORE ROOT PATHS ---
// Path: edgarcayce/app/...
const APP_ROOT_COLLECTION = 'edgarcayce';
const APP_ROOT_DOC = 'app';

const CONTENT_COLLECTION = 'pages';
const MAIN_CONTENT_DOC = 'mainContent';

const USERS_COLLECTION = 'users'; // edgarcayce/app/users/{userId}
const THREADS_COLLECTION = 'threads';
const SETTINGS_COLLECTION = 'settings'; 
const PREFERENCES_DOC = 'preferences'; // .../users/{userId}/settings/preferences

const WIKI_META_COLLECTION = 'wikiMetadata';
const WIKI_ARTICLES_COLLECTION = 'wiki';
const BOOKMARKS_COLLECTION = 'bookmarks';
const BOOKMARK_CATEGORIES_COLLECTION = 'bookmarkCategories';
const INVOICES_COLLECTION = 'invoices';
const PAYMENTS_COLLECTION = 'payments';
const COMMUNITY_POSTS_COLLECTION = 'communityPosts';

export const onPageContentUpdate = (callback: (data: Record<string, string> | null) => void) => {
  if (!db) {
      setTimeout(() => callback(null), 100);
      return () => {};
  }
  const contentDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, CONTENT_COLLECTION, MAIN_CONTENT_DOC);
  return onSnapshot(contentDocRef, (docSnap) => {
    callback(toPlainData(docSnap));
  }, (error) => {
      handleFirestoreError(error, OperationType.GET, contentDocRef.path);
  });
};

export const onCommunityPostsUpdate = (callback: (posts: Post[]) => void) => {
  if (!db) return () => {};
  const colRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, COMMUNITY_POSTS_COLLECTION);
  return onSnapshot(colRef, (snapshot) => {
    const posts: Post[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) posts.push({ ...data, id: docSnap.id } as Post);
    });
    // Sort by timestamp if possible, otherwise by ID (reverse)
    posts.sort((a, b) => {
        const timeA = typeof a.timestamp === 'number' ? a.timestamp : parseInt(a.id.split('-')[1]) || 0;
        const timeB = typeof b.timestamp === 'number' ? b.timestamp : parseInt(b.id.split('-')[1]) || 0;
        return timeB - timeA;
    });
    callback(posts);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, colRef.path);
  });
};

export const saveCommunityPost = async (post: Post): Promise<boolean> => {
  if (!db) {
    console.error("Firestore DB not initialized");
    return false;
  }
  if (!canWriteToFirestore()) {
    console.warn("Write blocked: canWriteToFirestore returned false (no currentUser)");
    return false;
  }

  const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, COMMUNITY_POSTS_COLLECTION, post.id);
  console.log('[DEBUG] Attempting to save post to path:', docRef.path);
  console.log('[DEBUG] Post data:', safeStringify(post, 2));
  
  try {
    const data = { 
        ...post, 
        timestamp: typeof post.timestamp === 'number' ? post.timestamp : Date.now() 
    };
    // Ensure no undefined values
    Object.keys(data).forEach(key => (data as any)[key] === undefined && delete (data as any)[key]);
    
    await setDoc(docRef, data, { merge: true });
    console.log('[DEBUG] Post saved successfully to Firestore!');
    return true;
  } catch (e: any) {
    console.error('[DEBUG] Firestore setDoc failed:', e);
    handleFirestoreError(e, OperationType.WRITE, docRef.path);
    return false;
  }
};

export const deleteCommunityPost = async (postId: string): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;
  
  const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, COMMUNITY_POSTS_COLLECTION, postId);
  try {
    await deleteDoc(docRef);
    return true;
  } catch (e: any) {
    handleFirestoreError(e, OperationType.DELETE, docRef.path);
    return false;
  }
};
export const updatePageContent = async (key: string, content: string): Promise<boolean> => {
  if (!db) throw new Error("Firestore not initialized.");
  if (!canWriteToFirestore()) return false;

  const contentDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, CONTENT_COLLECTION, MAIN_CONTENT_DOC);
  try {
    await setDoc(contentDocRef, { [key]: content }, { merge: true });
    return true;
  } catch (e: any) {
    handleFirestoreError(e, OperationType.WRITE, contentDocRef.path);
    return false;
  }
};

// --- User Settings Sync (Theme, FontSize, Language) ---

export const onUserSettingsUpdate = (userId: string, callback: (settings: any) => void) => {
  if (!db) return () => {};
  // Path: edgarcayce/app/users/{userId}/settings/preferences
  const settingsDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, SETTINGS_COLLECTION, PREFERENCES_DOC);
  return onSnapshot(settingsDocRef, (docSnap) => {
    callback(toPlainData(docSnap));
  }, (error) => {
    handleFirestoreError(error, OperationType.GET, settingsDocRef.path);
  });
};

export const getUserSettings = async (userId: string): Promise<any | null> => {
  if (!db) return null;
  const settingsDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, SETTINGS_COLLECTION, PREFERENCES_DOC);
  try {
    const docSnap = await getDoc(settingsDocRef);
    return toPlainData(docSnap);
  } catch (e: any) {
    handleFirestoreError(e, OperationType.GET, settingsDocRef.path);
    return null;
  }
};

export const saveUserSettings = async (userId: string, settings: any): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  const settingsDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, SETTINGS_COLLECTION, PREFERENCES_DOC);
  try {
    // Path: edgarcayce/app/users/{userId}/settings/preferences
    await setDoc(settingsDocRef, settings, { merge: true });
    console.log("Settings saved to Firestore");
    return true;
  } catch (e: any) {
    handleFirestoreError(e, OperationType.WRITE, settingsDocRef.path);
    return false;
  }
};

// --- Threads Sync ---

export const onThreadsUpdate = (userId: string, callback: (threads: DiscussionThread[]) => void) => {
  if (!db) return () => {};
  const threadsRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, THREADS_COLLECTION);
  return onSnapshot(threadsRef, (snapshot) => {
    const threads: DiscussionThread[] = [];
    snapshot.forEach((docSnap) => {
      const thread = toPlainData(docSnap);
      if (thread) threads.push({ ...thread, id: docSnap.id });
    });
    threads.sort((a, b) => (b.lastUpdated || 0) - (a.lastUpdated || 0));
    callback(threads);
  }, (error) => {
    handleFirestoreError(error, OperationType.LIST, threadsRef.path);
  });
};

export const saveThreadToFirestore = async (userId: string, thread: DiscussionThread): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  const threadRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, THREADS_COLLECTION, thread.id);
  try {
    const cleanThread = {
        id: thread.id,
        query: thread.query,
        title: thread.title,
        history: thread.history.map(m => ({ role: m.role, text: m.text })),
        lastUpdated: thread.lastUpdated
    };
    await setDoc(threadRef, cleanThread);
    return true;
  } catch (e: any) {
    handleFirestoreError(e, OperationType.WRITE, threadRef.path);
    return false;
  }
};

export const deleteThreadFromFirestore = async (userId: string, threadId: string): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  const threadRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, THREADS_COLLECTION, threadId);
  try {
    await deleteDoc(threadRef);
    return true;
  } catch (e: any) {
    handleFirestoreError(e, OperationType.DELETE, threadRef.path);
    return false;
  }
};

// --- Wiki Sync ---

export const subscribeToWikiMetadata = (callback: (data: Record<string, Partial<ArchiveFile>>) => void) => {
  if (!db) return () => {};
  const colRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_META_COLLECTION);
  return onSnapshot(colRef, (snapshot) => {
    const data: Record<string, Partial<ArchiveFile>> = {};
    snapshot.forEach((docSnap) => {
      data[docSnap.id] = toPlainData(docSnap);
    });
    callback(data);
  });
};

export const saveWikiMetadata = async (fileId: string, data: Partial<ArchiveFile>): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_META_COLLECTION, fileId);
    const safeData = {
        name: data.name || '',
        category: data.category || '',
        imageUrl: data.imageUrl || '',
        lastModified: data.lastModified || '',
        author: data.author || '',
        status: data.status || 'published'
    };
    await setDoc(docRef, safeData, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save wiki metadata:", e.message || e);
    return false;
  }
};

export const subscribeToWikiArticles = (callback: (articles: ArchiveFile[]) => void) => {
  if (!db) return () => {};
  const colRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_ARTICLES_COLLECTION);
  return onSnapshot(colRef, (snapshot) => {
    const articles: ArchiveFile[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) {
        articles.push({
          id: docSnap.id,
          name: data.name || data.title || 'Untitled',
          type: 'html',
          path: data.path || `firestore://${docSnap.id}`,
          category: data.category || 'General',
          lastModified: data.lastModified,
          size: data.size || '2KB',
          imageUrl: data.imageUrl,
          description: data.description,
          author: data.author,
          status: data.status || 'published'
        } as ArchiveFile);
      }
    });
    callback(articles);
  });
};

export const subscribeToArticle = (articleId: string, callback: (data: any) => void) => {
  if (!db) {
    callback(null);
    return () => {}; 
  }
  const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_ARTICLES_COLLECTION, articleId);
  return onSnapshot(docRef, 
    (docSnap) => {
      callback(toPlainData(docSnap));
    },
    (error) => {
      console.error("Error subscribing to article:", error.message || error);
      callback(null);
    }
  );
};

export const getWikiArticle = async (articleId: string) => {
    if (!db) throw new Error("Firestore not initialized");
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_ARTICLES_COLLECTION, articleId);
    try {
        const snap = await getDoc(docRef);
        return snap.exists() ? toPlainData(snap) : null;
    } catch (e) {
        console.warn("Failed to get article (possibly permissions):", e);
        return null;
    }
};

export const saveWikiArticle = async (articleId: string, data: any): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_ARTICLES_COLLECTION, articleId);
    
    // Create a mutable copy of the payload to sanitize it
    const payload: { [key: string]: any } = {
        ...data,
        updatedAt: Date.now()
    };

    // Sanitize the payload: Firestore does not accept 'undefined' values.
    // We iterate over the object and remove any keys with an undefined value.
    Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) {
            delete payload[key];
        }
    });

    if (typeof payload.content !== 'string') {
        payload.content = String(payload.content || '');
    }
    
    await setDoc(docRef, payload, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save wiki article:", e.message || e);
    return false;
  }
};

export const deleteWikiArticle = async (articleId: string): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, WIKI_ARTICLES_COLLECTION, articleId);
    await deleteDoc(docRef);
    return true;
  } catch (e: any) {
    console.error("Failed to delete wiki article:", e.message || e);
    return false;
  }
};

// --- Bookmarks Sync ---

export const onBookmarksUpdate = (userId: string, callback: (bookmarks: any[]) => void) => {
  if (!db) return () => {};
  const bookmarksRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARKS_COLLECTION);
  return onSnapshot(bookmarksRef, (snapshot) => {
    const bookmarks: any[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) bookmarks.push({ ...data, id: docSnap.id });
    });
    bookmarks.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    callback(bookmarks);
  });
};

export const saveBookmark = async (userId: string, bookmark: any): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARKS_COLLECTION, bookmark.id);
    const data = { ...bookmark, timestamp: bookmark.timestamp || Date.now() };
    // Remove undefined values as Firestore doesn't support them
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save bookmark:", e.message || e);
    return false;
  }
};

export const deleteBookmark = async (userId: string, bookmarkId: string): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARKS_COLLECTION, bookmarkId);
    await deleteDoc(docRef);
    return true;
  } catch (e: any) {
    console.error("Failed to delete bookmark:", e.message || e);
    return false;
  }
};

// --- Bookmark Categories Sync ---

export const onBookmarkCategoriesUpdate = (userId: string, callback: (categories: any[]) => void) => {
  if (!db) return () => {};
  const categoriesRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARK_CATEGORIES_COLLECTION);
  return onSnapshot(categoriesRef, (snapshot) => {
    const categories: any[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) categories.push({ ...data, id: docSnap.id });
    });
    callback(categories);
  });
};

export const saveBookmarkCategory = async (userId: string, category: any): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARK_CATEGORIES_COLLECTION, category.id);
    const data = { ...category };
    // Remove undefined values as Firestore doesn't support them
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key]);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save bookmark category:", e.message || e);
    return false;
  }
};

export const deleteBookmarkCategory = async (userId: string, categoryId: string): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, BOOKMARK_CATEGORIES_COLLECTION, categoryId);
    await deleteDoc(docRef);
    return true;
  } catch (e: any) {
    console.error("Failed to delete bookmark category:", e.message || e);
    return false;
  }
};

// --- Invoices & Payments Sync ---

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  status: 'paid' | 'pending';
  billingCycle?: string;
  usageDetails?: { day: number, usage: number }[];
}

export interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  type: 'topup' | 'bill-payment';
  method: 'google-pay' | 'stripe';
  status: 'success' | 'failed';
}

export const onInvoicesUpdate = (userId: string, callback: (invoices: Invoice[]) => void) => {
  if (!db) return () => {};
  const invoicesRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, INVOICES_COLLECTION);
  return onSnapshot(invoicesRef, (snapshot) => {
    const invoices: Invoice[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) invoices.push({ ...data, id: docSnap.id } as Invoice);
    });
    invoices.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    callback(invoices);
  });
};

export const onPaymentsUpdate = (userId: string, callback: (payments: PaymentRecord[]) => void) => {
  if (!db) return () => {};
  const paymentsRef = collection(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, PAYMENTS_COLLECTION);
  return onSnapshot(paymentsRef, (snapshot) => {
    const payments: PaymentRecord[] = [];
    snapshot.forEach((docSnap) => {
      const data = toPlainData(docSnap);
      if (data) payments.push({ ...data, id: docSnap.id } as PaymentRecord);
    });
    payments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    callback(payments);
  });
};

export const savePaymentRecord = async (userId: string, payment: PaymentRecord): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, PAYMENTS_COLLECTION, payment.id);
    const data = { ...payment };
    Object.keys(data).forEach(key => (data as any)[key] === undefined && delete (data as any)[key]);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save payment record:", e.message || e);
    return false;
  }
};

export const saveInvoice = async (userId: string, invoice: Invoice): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, INVOICES_COLLECTION, invoice.id);
    const data = { ...invoice };
    Object.keys(data).forEach(key => (data as any)[key] === undefined && delete (data as any)[key]);
    await setDoc(docRef, data, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to save invoice:", e.message || e);
    return false;
  }
};

export const updateInvoiceStatus = async (userId: string, invoiceId: string, status: 'paid' | 'pending'): Promise<boolean> => {
  if (!db) return false;
  if (!canWriteToFirestore()) return false;

  try {
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, INVOICES_COLLECTION, invoiceId);
    await setDoc(docRef, { status }, { merge: true });
    return true;
  } catch (e: any) {
    console.error("Failed to update invoice status:", e.message || e);
    return false;
  }
};

export const saveAppRegistration = async (userId: string, appId: string): Promise<boolean> => {
    if (!db) return false;
    if (!canWriteToFirestore()) return false;
    const docRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId);
    try {
        await setDoc(docRef, { 
            registeredApps: { [appId]: Date.now() },
            lastLoginApp: appId,
            lastLoginAt: Date.now(),
            email: auth.currentUser?.email || '',
            name: auth.currentUser?.displayName || 'User'
        }, { merge: true });
        return true;
    } catch (e: any) {
        handleFirestoreError(e, OperationType.WRITE, docRef.path);
        return false;
    }
};

export const onAiUsageUpdate = (userId: string, callback: (usage: any) => void) => {
    if (!db) return () => {};
    const usageDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, 'usage', 'ai');
    return onSnapshot(usageDocRef, (docSnap) => {
        callback(toPlainData(docSnap));
    }, (error) => {
        // Silently fail if usage doc doesn't exist yet
        if (error.code !== 'permission-denied') {
            handleFirestoreError(error, OperationType.GET, usageDocRef.path);
        }
    });
};

export const saveAiUsage = async (userId: string, usage: any): Promise<boolean> => {
    if (!db) return false;
    if (!canWriteToFirestore()) return false;
    const usageDocRef = doc(db, APP_ROOT_COLLECTION, APP_ROOT_DOC, USERS_COLLECTION, userId, 'usage', 'ai');
    try {
        await setDoc(usageDocRef, usage, { merge: true });
        return true;
    } catch (e: any) {
        handleFirestoreError(e, OperationType.WRITE, usageDocRef.path);
        return false;
    }
};
